import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Trial } from '../../models/trial';
import { EmotionOption } from '../../models/emotionOptionEnum';
import { ClassificationChoicesComponent } from '../classification-choices/classification-choices.component';
import { IntensityRatingComponent } from '../intensity-rating/intensity-rating.component';
import { TrialStage } from '../../models/trialStageEnum';
import { DataService } from '../../services/data.service';
import { interval, Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-experiment-trial',
  imports: [
    CommonModule,
    ClassificationChoicesComponent,
    IntensityRatingComponent,
  ],
  templateUrl: './experiment-trial.component.html',
  styleUrl: './experiment-trial.component.scss',
})
export class ExperimentTrialComponent implements OnInit, OnDestroy {
  @Input() trial: Trial | undefined = undefined;
  @Input() isVisible = false;
  @Input() currentTrialStage: TrialStage = TrialStage.Classification;
  @Input() trialImgSrc: string = '';
  @Output() practiceClassificationSelected = new EventEmitter<EmotionOption>();
  @Output() saveClassificationResponse = new EventEmitter<EmotionOption>();
  @Output() saveIntensityResponse = new EventEmitter<number>();
  @Output() completeTrial = new EventEmitter<void>();

  showingPracticeFeedback = false;
  practiceFeedbackMessage = '';
  private feedbackTimeout: any;

  // Should set to false in production
  private subscription!: Subscription;
  testResponseTime = false;
  classificationResponseTimeForTesting = 0;
  intensityRatingResponseTimeForTesting = 0;

  constructor(private dataService: DataService) {}

  ngOnInit() {
    if (this.testResponseTime) {
      this.subscription = interval(500).subscribe(() => {
        this.getClassificationResponseTimeForTesting();
        this.getIntensityRatingResponseTimeForTesting();
      });
    }
  }

  ngOnDestroy() {
    if (this.testResponseTime) {
      this.subscription.unsubscribe();
    }
    if (this.feedbackTimeout) {
      clearTimeout(this.feedbackTimeout);
    }
  }

  emotionChoices(): EmotionOption[] {
    return Object.values(EmotionOption);
  }

  isPractice(): boolean {
    return this.trial?.trialNumber === 0;
  }

  showClassification(): boolean {
    return (
      this.isVisible && this.currentTrialStage === TrialStage.Classification
    );
  }

  showIntensityRating(): boolean {
    return (
      this.isVisible && this.currentTrialStage === TrialStage.IntensityRating
    );
  }

  classicationChoiceSelected(selectedChoice: EmotionOption) {
    if (this.isPractice()) {
      const isCorrect = selectedChoice === this.trial?.correctAnswer;
      this.showingPracticeFeedback = true;
      this.practiceFeedbackMessage = isCorrect
        ? '你選對了！'
        : '不太正確。請再試一次。';

      // After 2 seconds, hide feedback and proceed
      this.feedbackTimeout = setTimeout(() => {
        this.showingPracticeFeedback = false;
        this.practiceFeedbackMessage = '';
        if (isCorrect) {
          if (this.trial?.correctAnswer === EmotionOption.NEUTRAL) {
            // For NEUTRAL emotions in practice, skip intensity rating
            this.completeTrial.emit();
          } else {
            this.practiceClassificationSelected.emit(selectedChoice);
          }
        } else {
          this.practiceClassificationSelected.emit(selectedChoice);
        }
      }, 1000);
    } else {
      this.saveClassificationResponse.emit(selectedChoice);
    }
  }

  intensityRatingConfirmed(confirmedRating: number) {
    if (!this.isPractice()) {
      this.saveIntensityResponse.emit(confirmedRating);
    }
    this.completeTrial.emit();
  }

  getClassificationResponseTimeForTesting() {
    this.classificationResponseTimeForTesting =
      this.dataService.getCurrentEmotionClassificationResponseTime();
  }

  getIntensityRatingResponseTimeForTesting() {
    this.intensityRatingResponseTimeForTesting =
      this.dataService.getCurrentIntensityResponseTime();
  }
}
